import { feedback, feedbackV2, listsV2 } from '@ecosystem/newron-design-system';
import React, { useCallback, useMemo, useState } from 'react';

import { ColumnConfiguration, RestrictedColData, Tab } from '../../types';
import { ContextBodyProps } from './types';
import { getFilteredData } from '../../utils';
import { DefaultContent } from './DefaultContent';
import { GroupedTableContent } from './GroupedTableContent';
import { NO_SEARCH_RESULTS_MESSAGE, SearchArea } from './SearchArea';

const { AlertSize } = feedback;
const { Alert } = feedbackV2;

export const NO_DATA_MESSAGE = 'No data can be displayed, please close the context selector and try again later.';

const getColumnHeader = (columnConfig: ColumnConfiguration[], columnKey: string) =>
  columnConfig.find(col => col.columnKey === columnKey)?.columnName || columnKey;

const getAlertMessage = (
  restrictedColumn: RestrictedColData,
  isAnyRowSelected: boolean,
  selectedTab: Tab,
  selectedGroup: string,
  columnConfig: ColumnConfiguration[]
) => {
  // Don't show alert message if no rows are selected or no restricted column is set.
  if (!restrictedColumn || !isAnyRowSelected) return;

  const { columnKey, restrictedValues, selectedValue, alertMessage: receivedAlertMessage } = restrictedColumn;
  const isGroupedTab = selectedTab.showGroupedTablesByValue;
  const isSelectedGroupRestricted = restrictedValues?.includes(selectedGroup);
  const isSelectedValRestricted = restrictedValues?.includes(selectedValue);

  // If there are restricted values, but neither the selected value nor group is restricted, do not show alert.
  if (restrictedValues?.length && !isSelectedGroupRestricted && !isSelectedValRestricted) return;

  const columnHeader = getColumnHeader(columnConfig, columnKey).toLowerCase();
  const restrictedValue = isSelectedGroupRestricted && !isSelectedValRestricted ? selectedGroup : selectedValue;
  const alertMessage = restrictedValues?.length
    ? `You cannot combine ${restrictedValue} data with data from any other ${columnHeader}`
    : receivedAlertMessage;

  // Show alert if not grouped tab or not grouped by restricted column
  if (!isGroupedTab || selectedTab.value !== columnKey) return alertMessage;

  // Show alert if selected group is not equal to selected value
  if (selectedGroup !== selectedValue) return alertMessage;
};

const ContextBodyContainer: React.FC<{
  children: React.ReactNode;
  searchPlaceHolder: Tab['searchPlaceHolder'];
  onSearch: (query: string) => void;
  alertMessage?: string;
}> = ({ children, searchPlaceHolder, onSearch, alertMessage }) => (
  <div className="flex flex-column gap-3 relative h-full text-left">
    <SearchArea placeholder={searchPlaceHolder} onSearch={onSearch} />
    {children}
    {alertMessage && (
      <div className="bottom-0 w-full">
        <Alert type="warn" size={AlertSize.SMALL} title={alertMessage} />
      </div>
    )}
  </div>
);

export const ContextBody: React.FC<ContextBodyProps> = ({
  tableInfo,
  selectedRows,
  onRowSelection,
  selectedTab,
  rowSelectionMode = listsV2.RowSelectionMode.Multiple,
  restrictedColumn,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = React.useState<string>('');
  const isAnyRowSelected = useMemo(() => Object.keys(selectedRows ?? {}).length > 0, [selectedRows]);
  const isDataAvailable = useMemo(
    () => !!(tableInfo.data?.length && tableInfo.columnConfig?.length),
    [tableInfo.data, tableInfo.columnConfig]
  );
  const alertMessage = useMemo(
    () => getAlertMessage(restrictedColumn, isAnyRowSelected, selectedTab, selectedGroup, tableInfo.columnConfig),
    [restrictedColumn, isAnyRowSelected, selectedTab, selectedGroup, tableInfo.columnConfig]
  );

  // Update alert message based on group selection changes.
  const handleGroupSelect = useCallback((selectedGroup: string) => {
    setSelectedGroup(selectedGroup);
  }, []);

  /**
   * Filters table rows based on a search term.
   * - Searches only in `searchableColumns` (or all columns if none are marked searchable).
   * - Matches against string, number, and NonPrimitiveType values.
   * - If search term is empty, resets to the full data set.
   */
  const filteredRows = useMemo(() => {
    if (selectedTab.showGroupedTablesByValue) return tableInfo.data;
    return getFilteredData(tableInfo.data, searchTerm, tableInfo.columnConfig, selectedTab.value);
  }, [tableInfo.data, searchTerm, selectedTab.showGroupedTablesByValue, selectedTab.value, tableInfo.columnConfig]);

  const handleOnSearch = useCallback((raw: string) => {
    setSearchTerm(raw);
  }, []);

  if (rowSelectionMode === listsV2.RowSelectionMode.Single && selectedRows) {
    const keys = Object.keys(selectedRows ?? {});
    if (keys.length > 1) {
      throw new Error(`Invalid selectedRows: rowSelectionMode is 'single', but received ${keys.length} selected rows.`);
    }
  }

  if (rowSelectionMode === listsV2.RowSelectionMode.Single && restrictedColumn) {
    throw new Error('restrictedColumn is not supported in single selection mode.');
  }

  if (!isDataAvailable) {
    return (
      <ContextBodyContainer searchPlaceHolder={selectedTab.searchPlaceHolder} onSearch={handleOnSearch}>
        <Alert type="error" size={AlertSize.SMALL} title={NO_DATA_MESSAGE} />
      </ContextBodyContainer>
    );
  }

  const contentProps = {
    tableInfo: {
      ...tableInfo,
      data: selectedTab.showGroupedTablesByValue ? tableInfo.data : filteredRows,
    },
    selectedRows,
    onRowSelection,
    rowSelectionMode,
    disableSelectAllButton: !!restrictedColumn,
  };
  const renderContent = () => {
    if (selectedTab.showGroupedTablesByValue) {
      return (
        <GroupedTableContent
          {...contentProps}
          groupKey={selectedTab.value}
          searchTerm={searchTerm}
          onSelectGroup={handleGroupSelect}
        />
      );
    }

    if (filteredRows?.length) {
      return <DefaultContent {...contentProps} />;
    }

    return <Alert type="info" size={AlertSize.SMALL} title={NO_SEARCH_RESULTS_MESSAGE} />;
  };

  return (
    <ContextBodyContainer
      searchPlaceHolder={selectedTab.searchPlaceHolder}
      onSearch={handleOnSearch}
      alertMessage={alertMessage}
    >
      {renderContent()}
    </ContextBodyContainer>
  );
};
